import React, { useEffect } from 'react';
import { useForm, FormContextValues } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  DEPENDENTS,
  HAVE_OR_NOT,
  PROFESSIONS,
  PURPOSES,
  PACProps,
  PAC_FORM_FIELD_LABELS,
  PAC_FORM_FIELD_NAMES,
  PAC_FORM_FIELD_PLACEHOLDERS,
} from 'shared/constants/ekyc';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import Picker from 'shared/modules/common/components/molecules/Picker';
import validator, { Rule } from 'shared/utils/validator';

type Props = {
  onNextButtonPress: () => void;
  onReturnButtonPress: () => void;
  form: FormContextValues<PACProps>;
  onFocus?: () => void;
  onBlur?: () => void;
  onFieldBlur?: (
    field:
      | 'profession'
      | 'purpose'
      | 'salary'
      | 'numberOfDependents'
      | 'rentOrMortgage'
  ) => void;
};

const PAC = (props: Props) => {
  const {
    onNextButtonPress,
    onReturnButtonPress,
    form,
    onFocus,
    onBlur,
    onFieldBlur,
  } = props;
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  const getOptionListByFieldName = (fieldName: keyof PACProps) => {
    switch (fieldName) {
      case 'profession':
        return PROFESSIONS;
      case 'purpose':
        return PURPOSES;
      case 'numberOfDependents':
        return DEPENDENTS;
      case 'rentOrMortgage':
        return HAVE_OR_NOT;
      default:
        return [];
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.panel}>
        <View
          style={styles.form}
          {...Platform.select({ web: { accessibilityRole: 'form' } })}
        >
          {PAC_FORM_FIELD_NAMES.map((field, index) => {
            if (field === 'salary') {
              return (
                <TextInput
                  key={index}
                  value={form.getValues(field)}
                  label={PAC_FORM_FIELD_LABELS[index]}
                  placeholder={PAC_FORM_FIELD_PLACEHOLDERS[index]}
                  onChangeText={(text) => form.setValue(field, text)}
                  onBlur={() => {
                    onFieldBlur?.(field);
                    form.triggerValidation(field);
                  }}
                  message={(form.errors[field]?.message as string) || ''}
                  state={form.errors[field]?.message ? 'error' : undefined}
                  id={`ekyc-address-form-${field.toLowerCase()}-field`}
                  style={styles.marginBottom}
                />
              );
            }

            const options = getOptionListByFieldName(field);

            return (
              <Picker
                key={index}
                value={form.getValues(field)}
                label={PAC_FORM_FIELD_LABELS[index]}
                placeholder={PAC_FORM_FIELD_PLACEHOLDERS[index]}
                options={options.map((place) => ({
                  label: place,
                  value: place,
                }))}
                selectButtonText="保存する"
                onSelect={(text) => form.setValue(field, text)}
                onBlur={() => {
                  onFieldBlur?.(field);
                  form.triggerValidation(field);
                }}
                message={(form.errors[field]?.message as string) || ''}
                state={form.errors[field]?.message ? 'error' : undefined}
                style={styles.marginBottom}
                dialogStyle={styles.pickerDialog}
                showAsDialog={isBigScreen}
                id={`ekyc-address-form-${field}-picker`}
              />
            );
          })}
        </View>
      </View>
      <View
        style={[
          styles.buttonContainer,
          isBigScreen && styles.buttonContainerBigScreen,
        ]}
      >
        <Button
          title="次へ"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            if (isValid) {
              onNextButtonPress();
            }
          }}
          id="ekyc-pac-form-next-button"
          disabled={!form.formState.dirty}
        />
        {isBigScreen && (
          <Button
            onPress={onReturnButtonPress}
            title="戻る"
            variant="outlined"
            style={styles.returnButton}
            id="ekyc-pac-form-return-button"
          />
        )}
      </View>
    </View>
  );
};

export const useFormPACWithValidations = (props: PACProps) => {
  const form = useForm<PACProps>({
    defaultValues: props,
  });

  const basicRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainSpace',
    'mustNotContainNonPrintableChars',
  ];

  useEffect(() => {
    PAC_FORM_FIELD_NAMES.forEach((name) => {
      if (name === 'salary') {
        form.register(
          { name },
          {
            validate: validator.getRules([...basicRules, 'mustBeNumber']),
          }
        );
      } else {
        form.register(
          {
            name,
          },
          {
            validate: validator.getRules(basicRules),
          }
        );
      }
    });
  }, [form.register]);

  form.watch();

  return form;
};

const styles = StyleSheet.create({
  root: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  panel: {
    padding: 24,
    borderRadius: 16,
    backgroundColor: '#fff',
    marginVertical: 12,
  },
  form: {
    flex: 1,
    justifyContent: 'space-between',
  },
  pickerDialog: {
    width: 424,
  },
  marginBottom: {
    marginBottom: 24,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 12,
  },
  buttonContainerBigScreen: {
    paddingTop: 48,
  },
  returnButton: {
    marginTop: 18,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
});

export default PAC;
